/**
 * Casas decimais do ROI EXIBIDO.
 *
 * ## Por que 2, e não as 3 do gravado
 *
 * O snapshot grava com 3 casas (`MAX_DECIMALS` do `monthly-snapshot.service`)
 * porque é armazenamento: o objetivo lá é cortar o ruído de ponto flutuante
 * (`3.4000000000000004`), não produzir texto.
 *
 * Exibir 3 casas em `pt-BR` foi um erro meu, reportado em produção como "os
 * números deram muito altos": `5.157` vira `"5,157x"`, que se lê como 5157. Um
 * grupo de milhar tem exatamente 3 dígitos, então 3 casas decimais é a largura
 * em que o separador decimal fica indistinguível do de milhar — e trocar a
 * vírgula por ponto não salva, porque em pt-BR o ponto JÁ é o de milhar.
 *
 * Com 2 casas a ambiguidade some: `"5,16x"` não tem como ser lido como milhar.
 *
 * ## A regra que continua valendo
 *
 * Exibir NUNCA pode mostrar mais casas do que o gravado tem, senão o painel
 * inventaria precisão que o Brain não tem. `2 <= 3` satisfaz isso, e há teste
 * cruzando as duas constantes.
 */
export const ROI_MAX_DECIMALS = 2

/**
 * Casas do ARMAZENAMENTO. Espelha `MAX_DECIMALS` do `monthly-snapshot.service`.
 *
 * Não é usada para exibir — serve para o passo intermediário abaixo.
 */
const STORAGE_DECIMALS = 3

/**
 * Arredonda como o snapshot arredonda antes de gravar.
 *
 * Cópia deliberada do `roundNumber` do backend: os dois pacotes não podem
 * compartilhar módulo (o `rootDir` do build backend é `src/backend`), e o teste
 * em `monthly-snapshot.service.test.ts` cruza as duas implementações.
 *
 * Arredonda a MAGNITUDE e devolve o sinal, em vez de `Math.round` puro:
 * `Math.round(-2.5)` é -2, e ROI negativo é comum aqui.
 */
function roundLikeStorage(value: number): number {
  if (!Number.isFinite(value) || Number.isInteger(value)) return value
  const factor = 10 ** STORAGE_DECIMALS
  return (Math.sign(value) * Math.round(Math.abs(value) * factor)) / factor
}

/**
 * O número do ROI, sem o sufixo — cada chamador usa `x` ou `×` como já usava.
 *
 * ## O passo intermediário que parece supérfluo e não é
 *
 * Arredondar para as casas do ARMAZENAMENTO antes de formatar nas casas de
 * EXIBIÇÃO. Sem ele, o mesmo ROI sai diferente conforme a origem:
 *
 *     valor cru do pipeline   -1,2345 → exibe -1,23
 *     valor gravado no banco  -1,235  → exibe -1,24
 *
 * É arredondamento duplo em precisões diferentes. O painel recebe o valor cru e
 * o Brain lê o gravado, então a divergência apareceria exatamente entre as duas
 * telas que este arquivo existe para manter iguais.
 *
 * Passar pelo arredondamento de armazenamento primeiro torna os dois caminhos
 * idênticos — ele é idempotente, então aplicar no valor já gravado não muda nada.
 *
 * `pt-BR` para casar com a frase do `formatRoiInsight`, que sempre usou vírgula:
 * o card dizia "5.2x" logo acima de um texto dizendo "R$5,16", com dois
 * separadores diferentes na mesma tela.
 */
export function formatRoiValue(avgRoi: number): string {
  return roundLikeStorage(avgRoi).toLocaleString('pt-BR', { maximumFractionDigits: ROI_MAX_DECIMALS })
}

/**
 * Texto do ROI para leitura humana — uma implementação, quatro consumidores
 * (painel, PDF de segmento, PDF de empresa).
 *
 * ## O erro que isto corrige
 *
 * `roi` no backend é RETORNO LÍQUIDO: `(receita − custo) ÷ custo`
 * (`roi.service.ts`). Um ROI de 19,9 significa "sobrou 19,9x o investido"; o
 * múltiplo de RECEITA equivalente é 20,9 — sempre `roi + 1`.
 *
 * As quatro frases escreviam `Para cada R$1 investido, a Widde gerou R$${roi}
 * em vendas`, que é a leitura BRUTA de um número LÍQUIDO. Errava por uma unidade
 * inteira, e ia para PDF de cliente. (Subestimava, então não era exagero
 * comercial — mas continuava errado.)
 *
 * ## Por que a frase cita os dois números
 *
 * O card ao lado mostra "19,9x". Se o texto dissesse só "R$20,90", a diferença
 * entre os dois viraria pergunta. Citar os dois e nomear cada um fecha a
 * leitura sem obrigar ninguém a conhecer a convenção.
 *
 * Matematicamente: se `roi_i = (rec_i − custo_i) / custo_i`, o múltiplo de
 * receita de cada empresa é `roi_i + 1`, e a média dos múltiplos é
 * `média(roi_i) + 1`. Somar 1 à média é exato, não aproximação.
 *
 * ## O segundo erro: o nome da receita
 *
 * O numerador do ROI é `influencedRevenue`, que apesar do nome é a soma de
 * `influencedItemRevenue` — receita do ITEM que casou por título, não o valor
 * cheio do pedido (`segment-process.service.ts`). Isso é receita **atribuída**.
 * O PDF de segmento dizia "receita influenciada", que nesta base é outra coisa
 * (qualquer pedido de quem viu vídeo em 30d, valor cheio) e é sempre maior. As
 * quatro frases passam a dizer "receita atribuída", porque as quatro leem a
 * mesma base.
 */
export function formatRoiInsight(avgRoi: number): string {
  const fmt = (v: number) => v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  const multiploDeReceita = avgRoi + 1
  return (
    `Para cada R$1 investido, a Widde gerou R$${fmt(multiploDeReceita)} em receita atribuída — ` +
    `retorno líquido de R$${fmt(avgRoi)} (ROI ${formatRoiValue(avgRoi)}x).`
  )
}
